import { For, Show } from 'solid-js';
import { Link } from '@solidjs/router';
import clsx from 'clsx';
import { ErrorCatcher } from '../../tools/error-catcher';
import decodeHtmlEntities from '../../tools/decode-html-entities';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import { ArrowLeftIcon } from '../icons-library/arrow-left';
import { ChevronRightIcon } from '../icons-library/chevron-right';
import { BreadcrumbSchema } from '../structured-data';
import { Section } from '../../grid-system/section/section';

type MetaData = {
    postTitle: string;
    shortTitle: string;
    permalink: string;
}

type BreadcrumbProps = {
    pages: MetaData[];
    url: string;
};

export const Breadcrumb = ({ pages, url }: BreadcrumbProps) => {
    if (!pages || pages.length < 2) return null;

    const urlMap = url.split('/').filter(Boolean);

    const getLabel = (page: MetaData, index: number) => {
        if (page?.shortTitle || page?.postTitle) {
            return decodeHtmlEntities(page.shortTitle || page.postTitle);
        }
        let title = urlMap[index].charAt(0).toUpperCase() + urlMap[index].slice(1);
        title = title.replace(/-/g, ' ');
        return decodeHtmlEntities(title);
    };

    const getUrl = (page: MetaData, index: number) => {
        if (page?.permalink) {
            return removeTrailingSlash(page.permalink);
        }
        const formedUrl = '/' + urlMap.slice(0, index + 1).join('/');
        return removeTrailingSlash(formedUrl);
    };

    // Build items for structured data schema
    const schemaItems = pages.map((page, index) => ({
        name: getLabel(page, index),
        url: getUrl(page, index),
    }));

    const parentIndex = pages.length - 2;
    const parent = pages[parentIndex];

    const crumbClass = 'label-sm font-cvi-regular text-cvi-fg-muted';
    const crumbLinkClass = clsx(crumbClass, 'no-underline transition-colors duration-200 hover:text-cvi-fg-default');

    return (
        <ErrorCatcher componentName="Breadcrumb">
            <BreadcrumbSchema items={schemaItems} />
            <Section
                templateShorthand={[12]}
                widthType="bgFull"
                removeSpacingBlock={true}
                customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
            >
                <nav
                    aria-label="breadcrumb"
                    class="tablet-original:border-t tablet-original:border-cvi-border-muted"
                >
                    <Show when={parent}>
                        <ul class="flex items-center list-none p-0 m-0 tablet:hidden">
                            <li class="flex items-center pb-0">
                                <Link
                                    href={getUrl(parent, parentIndex)}
                                    class={clsx(crumbLinkClass, 'flex items-center gap-cvi-4 py-cvi-5')}
                                >
                                    <span class="flex items-center justify-center size-cvi-7 shrink-0">
                                        <ArrowLeftIcon fill="currentColor" size="small" />
                                    </span>
                                    {/* Trim the line box to the cap band so the label's ink,
                                        not its leading, centres against the arrow. */}
                                    <span class="[text-box:trim-both_cap_alphabetic]">{getLabel(parent, parentIndex)}</span>
                                </Link>
                            </li>
                        </ul>
                    </Show>

                    <ul class="hidden tablet:flex flex-wrap items-center list-none p-0 m-0">
                        <For each={pages}>{(page, index) => {
                            const isLast = () => index() === pages.length - 1;
                            return (
                                <li class="flex items-center">
                                    <Show when={index() !== 0}>
                                        <span class="flex items-center justify-center px-cvi-5 py-cvi-5 text-cvi-fg-muted opacity-40 shrink-0">
                                            <ChevronRightIcon fill="currentColor" size="xs" />
                                        </span>
                                    </Show>
                                    <Show
                                        when={!isLast()}
                                        fallback={
                                            <span
                                                class={clsx(crumbClass, 'py-cvi-5')}
                                                aria-current="page"
                                            >
                                                {getLabel(page, index())}
                                            </span>
                                        }
                                    >
                                        <Link
                                            href={getUrl(page, index())}
                                            class={clsx(crumbLinkClass, 'py-cvi-5')}
                                        >
                                            {getLabel(page, index())}
                                        </Link>
                                    </Show>
                                </li>
                            );
                        }}</For>
                    </ul>
                </nav>
            </Section>
        </ErrorCatcher>
    );
};
